import React, { useState } from "react";
import { Search, CheckCircle2, Info, AlertTriangle, XCircle } from "lucide-react";

/* Product UI Styleguide — Clean SaaS Monochrome (accent: teal) */

const ACCENT = "#0F766E";

const COLORS = [
  { label: "Primary Action", value: "#0F766E", style: { background: "#0F766E" } },
  { label: "Surface", value: "#FAFAFA", style: { background: "#FAFAFA", border: "1px solid #E5E5E5" } },
  { label: "Border", value: "#E5E5E5", style: { background: "#F5F5F5", border: "1px solid #D4D4D4" } },
  { label: "Focus Ring", value: "#0F766E / ring", style: { background: "#E6F4F1", border: "2px solid #0F766E", outline: "3px solid rgba(15,118,110,.18)" } },
  { label: "Gray 900", value: "#171717", style: { background: "#171717" } },
  { label: "Gray 700", value: "#404040", style: { background: "#404040" } },
  { label: "Gray 500", value: "#737373", style: { background: "#737373" } },
  { label: "Gray 300", value: "#D4D4D4", style: { background: "#D4D4D4" } },
  { label: "Gray 100", value: "#F5F5F5", style: { background: "#F5F5F5", border: "1px solid #E5E5E5" } },
];

const TYPE = [
  { name: "H1 Display", cls: "text-5xl font-extrabold tracking-tight leading-none", spec: "48 / 700" },
  { name: "H2 Heading", cls: "text-3xl font-bold tracking-tight", spec: "30 / 700" },
  { name: "H3 Subheading", cls: "text-xl font-semibold", spec: "20 / 600" },
  { name: "Body Large", cls: "text-lg", spec: "18 / 400" },
  { name: "Body Medium", cls: "text-base", spec: "16 / 400" },
  { name: "Body Small", cls: "text-sm", spec: "14 / 400" },
  { name: "Caption", cls: "text-xs uppercase tracking-widest text-neutral-500", spec: "12 / 500" },
];

const SPACING = [4, 8, 16, 24, 32, 64];
const RADII = [0, 4, 8, 12, 16];

const ELEVATION = [
  { level: "Shadow 1", spec: "0 2 4 -1", shadow: "0 2px 4px -1px rgba(23,23,23,.10)" },
  { level: "Shadow 2", spec: "0 4 8 -2", shadow: "0 4px 8px -2px rgba(23,23,23,.12)" },
  { level: "Shadow 3", spec: "0 8 16 -4", shadow: "0 8px 16px -4px rgba(23,23,23,.14)" },
  { level: "Shadow 4", spec: "0 16 32 -8", shadow: "0 16px 32px -8px rgba(23,23,23,.18)" },
];

const Section = ({ title, caption, children, testid }) => (
  <section data-testid={testid} className="border-t border-neutral-200/80 pt-6 first:border-t-0 first:pt-0">
    <div className="mb-5">
      <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">{title}</h3>
      {caption && <p className="mt-1 text-sm text-neutral-400">{caption}</p>}
    </div>
    {children}
  </section>
);

const Btn = ({ variant, state, children }) => {
  const base = "inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-150 w-full";
  const map = {
    primary: {
      Default: { background: ACCENT, color: "#fff" },
      Hover: { background: "#0B5B55", color: "#fff" },
      Active: { background: "#073F3B", color: "#fff", boxShadow: "0 0 0 3px rgba(15,118,110,.22)" },
    },
    secondary: {
      Default: { background: "#fff", color: "#171717", border: "1px solid #D4D4D4" },
      Hover: { background: "#F5F5F5", color: "#171717", border: "1px solid #A3A3A3" },
      Active: { background: "#E6F4F1", color: ACCENT, border: `1px solid ${ACCENT}` },
    },
    tertiary: {
      Default: { background: "transparent", color: "#404040" },
      Hover: { background: "#F5F5F5", color: "#171717" },
      Active: { background: "transparent", color: ACCENT, textDecoration: "underline" },
    },
  };
  return (
    <button data-testid={`btn-${variant}-${state.toLowerCase()}`} className={base} style={map[variant][state]}>
      {children}
    </button>
  );
};

const Chip = ({ children, filled, tone = "neutral" }) => {
  const tones = {
    neutral: { fill: { background: "#171717", color: "#fff" }, out: { border: "1px solid #D4D4D4", color: "#404040" } },
    accent: { fill: { background: "#E6F4F1", color: ACCENT, border: "1px solid #99D3CD" }, out: { border: `1px solid ${ACCENT}`, color: ACCENT } },
  };
  const s = filled ? tones[tone].fill : tones[tone].out;
  return (
    <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={s}>
      {children}
    </span>
  );
};

const ALERTS = [
  { Icon: CheckCircle2, text: "Changes saved", color: "#166534", bg: "#F0FDF4", bd: "#BBF7D0", label: "Success" },
  { Icon: Info, text: "New feature available", color: "#1D4ED8", bg: "#EFF6FF", bd: "#BFDBFE", label: "Info" },
  { Icon: AlertTriangle, text: "Session expiring", color: "#92400E", bg: "#FFFBEB", bd: "#FDE68A", label: "Warning" },
  { Icon: XCircle, text: "Failed to save", color: "#B91C1C", bg: "#FEF2F2", bd: "#FECACA", label: "Error" },
];

export default function Styleguide() {
  const [tab, setTab] = useState("Overview");

  return (
    <div
      data-testid="styleguide-page"
      className="min-h-screen bg-white text-neutral-900 antialiased"
      style={{
        backgroundImage:
          "linear-gradient(to right, rgba(23,23,23,.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(23,23,23,.045) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }}
    >
      <div className="mx-auto max-w-[1400px] px-6 py-16 sm:px-10 lg:py-24">
        {/* Header */}
        <header className="mb-16 max-w-4xl">
          <span className="mb-6 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]" style={{ background: "#E6F4F1", color: ACCENT }}>
            Design System
          </span>
          <h1 data-testid="styleguide-title" className="text-5xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            Product UI
            <br />
            Styleguide
          </h1>
          <p data-testid="styleguide-subtitle" className="mt-6 text-lg text-neutral-500">
            Version 1.0 <span className="mx-2 text-neutral-300">|</span> Juni 2026
          </p>
        </header>

        {/* 4 column grid */}
        <div className="grid grid-cols-1 gap-x-12 gap-y-14 md:grid-cols-2 xl:grid-cols-4">
          {/* Column 1 — Color Tokens */}
          <div className="space-y-14">
            <Section testid="sg-colors" title="Color Tokens" caption="Monochrome base + single teal accent.">
              <div className="space-y-4">
                {COLORS.map((c) => (
                  <div key={c.label}>
                    <div className="h-16 w-full rounded-lg" style={{ ...c.style }} />
                    <div className="mt-2">
                      <p className="text-sm font-medium">{c.label}</p>
                      <p className="text-xs text-neutral-400">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {/* Column 2 — Typography, Spacing, Radius */}
          <div className="space-y-14">
            <Section testid="sg-typography" title="Typography Scale" caption="Sans-serif, bold display headings.">
              <div className="space-y-5">
                {TYPE.map((t) => (
                  <div key={t.name}>
                    <p className={t.cls}>{t.name}</p>
                    <p className="mt-1 text-xs text-neutral-400">{t.spec}</p>
                  </div>
                ))}
                <p className="border-t border-neutral-200/80 pt-4 text-sm leading-relaxed text-neutral-600">
                  Product UI Styleguide — Clean SaaS Monochrome
                </p>
              </div>
            </Section>

            <Section testid="sg-spacing" title="Spacing Scale">
              <div className="space-y-3">
                {SPACING.map((s) => (
                  <div key={s} className="flex items-center gap-4">
                    <div className="rounded" style={{ width: s, height: s, background: ACCENT, minWidth: 4 }} />
                    <span className="text-sm text-neutral-500">{s}px</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section testid="sg-radius" title="Radius Scale">
              <div className="flex flex-wrap items-end gap-4">
                {RADII.map((r) => (
                  <div key={r} className="text-center">
                    <div className="h-14 w-14" style={{ borderRadius: r, background: "#F5F5F5", border: "1px solid #D4D4D4" }} />
                    <span className="mt-2 block text-xs text-neutral-500">{r}px</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {/* Column 3 — Elevation, Buttons */}
          <div className="space-y-14">
            <Section testid="sg-elevation" title="Elevation / Shadow">
              <div className="space-y-6">
                {ELEVATION.map((e) => (
                  <div key={e.level}>
                    <div className="flex h-20 items-center justify-center rounded-xl bg-white text-xs text-neutral-400" style={{ boxShadow: e.shadow }}>
                      Level {e.level.split(" ")[1]}
                    </div>
                    <p className="mt-2 text-xs text-neutral-500">
                      {e.level}: {e.spec}
                    </p>
                  </div>
                ))}
              </div>
            </Section>

            <Section testid="sg-buttons" title="Buttons" caption="Primary / Secondary / Tertiary × states.">
              <div className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-3">
                <span />
                {["Default", "Hover", "Active"].map((s) => (
                  <span key={s} className="text-center text-[10px] uppercase tracking-widest text-neutral-400">
                    {s}
                  </span>
                ))}
                {["primary", "secondary", "tertiary"].map((v) => (
                  <React.Fragment key={v}>
                    <span className="pr-2 text-xs capitalize text-neutral-500">{v}</span>
                    {["Default", "Hover", "Active"].map((s) => (
                      <Btn key={s} variant={v} state={s}>
                        Button
                      </Btn>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </Section>
          </div>

          {/* Column 4 — Inputs, Chips, Tabs, Alerts, Cards */}
          <div className="space-y-14">
            <Section testid="sg-inputs" title="Inputs">
              <div className="space-y-5">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    data-testid="sg-search-input"
                    placeholder="Search invoices"
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition-shadow duration-150 placeholder:text-neutral-400 focus:border-[#0F766E] focus:shadow-[0_0_0_3px_rgba(15,118,110,.18)]"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-neutral-600">Email Address</label>
                  <input
                    data-testid="sg-email-input"
                    placeholder="name@company.com"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none transition-shadow duration-150 placeholder:text-neutral-400 focus:border-[#0F766E] focus:shadow-[0_0_0_3px_rgba(15,118,110,.18)]"
                  />
                </div>
              </div>
            </Section>

            <Section testid="sg-chips" title="Chips">
              <div className="flex flex-wrap gap-2">
                <Chip filled tone="accent">Filter: Active</Chip>
                <Chip filled>Filter: Filled</Chip>
                <Chip>Status: Pending</Chip>
                <Chip tone="accent">Tag: New</Chip>
              </div>
            </Section>

            <Section testid="sg-tabs" title="Tabs">
              <div className="flex gap-6 border-b border-neutral-200">
                {["Overview", "Billing", "Team"].map((t) => (
                  <button
                    key={t}
                    data-testid={`sg-tab-${t.toLowerCase()}`}
                    onClick={() => setTab(t)}
                    className="relative -mb-px pb-3 text-sm font-medium transition-colors duration-150"
                    style={{ color: tab === t ? ACCENT : "#737373", borderBottom: `2px solid ${tab === t ? ACCENT : "transparent"}` }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </Section>

            <Section testid="sg-alerts" title="Alerts">
              <div className="space-y-3">
                {ALERTS.map(({ Icon, text, color, bg, bd, label }) => (
                  <div key={label} className="flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm" style={{ background: bg, border: `1px solid ${bd}`, color }}>
                    <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section testid="sg-cards" title="Cards">
              <div className="overflow-hidden rounded-xl bg-white" style={{ boxShadow: "0 4px 8px -2px rgba(23,23,23,.12)", border: "1px solid #E5E5E5" }}>
                <div className="flex h-32 items-center justify-center bg-neutral-100 text-xs text-neutral-400">Image placeholder</div>
                <div className="p-4">
                  <h4 className="text-base font-semibold">Title card</h4>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-500">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod.
                  </p>
                  <span className="mt-4 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium" style={{ background: "#F5F5F5", color: "#525252" }}>
                    Label
                  </span>
                </div>
              </div>
            </Section>
          </div>
        </div>

        <footer className="mt-20 border-t border-neutral-200/80 pt-6 text-xs text-neutral-400">
          Semua komponen di halaman ini memakai token spacing, radius, dan shadow yang didefinisikan di atas — the styleguide dogfoods its own system.
        </footer>
      </div>
    </div>
  );
}
